import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UrlSignature } from '../common/apiEndpoint';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  username: string;
  active: boolean;
  adminType: string;
}

export interface LoginError {
  error: string;
}

@Injectable({
  providedIn: 'root'
})
export class SuperloginService {
  private readonly TOKEN_KEY = 'sysadmin_token';
  private readonly USER_KEY = 'sysadmin_user';

  constructor(
    private http: HttpClient,
    private router: Router,
    private urlsignature: UrlSignature
  ) { }

 
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.urlsignature.sysadminloginurl, credentials)
      .pipe(
        tap(response => {
          // Store token and user info on successful login
          this.storeAuthData(response);
        }),
        catchError(this.handleError)
      );
  }

 
  private storeAuthData(response: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, response.token);
    localStorage.setItem(this.USER_KEY, JSON.stringify({
      username: response.username,
      active: response.active,
      adminType: response.adminType
    }));

    localStorage.setItem('administratorId', response.username); 
  }

 
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  
  getCurrentUser(): any {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }
  
 
  getUserRole(): string | null {
    const user = this.getCurrentUser();
    return user ? user.adminType : null;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    
    // Optional: Check if token is expired
    return !this.isTokenExpired(token);
  }


  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.router.navigate(['/super-login']);
  }

 
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'An unexpected error occurred';
    
    if (error.status === 401) {
      errorMessage = error.error?.error || 'Invalid credentials';
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to server';
    } else if (error.status >= 500) {
      errorMessage = 'Server error. Please try again later';
    } else if (error.error?.error) {
      errorMessage = error.error.error;
    }

    return throwError(() => new Error(errorMessage));
  };

 
  getAuthHeaders(): { [key: string]: string } {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }
}